import { useCallback, useMemo } from "react"
import { useNavigation, useRoute } from "@react-navigation/native"
import { useDispatch, useSelector } from "react-redux"
import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import { IS_WEB } from "@common/utils/mobileUtils"
import { apiRequest } from "@data/redux/actions/api"
import {
    clearAutoModeStartedActivity,
    clearRecentFinalizedActivity,
    pinStudentInDialogueAtomic,
    requestStudentActivityFocus,
    setCurrentStudentActivity,
    setOngoingCheckpointIds,
} from "@data/redux/actions/liveMode"
import { liveServiceV2 } from "@data/services/liveServiceV2"
import {
    dismissTutorEvent,
    markTutorEventSamSent,
} from "@data/redux/reducers/data/tutorEventsPanel"
import { EVENT_TYPES } from "./eventsCatalog"

const checkpointTypeForEvent = (eventType) => {
    if (eventType === EVENT_TYPES.HAND_RAISED) return "raise_hand"
    if (eventType === EVENT_TYPES.SILENCE) return "silent_mode_10min"
    return null
}

const displayNameOf = (student) =>
    student?.firstname || student?.display_name || "L'élève"

export const useTutorEventsActions = ({
    liveSessionId,
    students,
    currentUser,
    onPinStudent,
}) => {
    const dispatch = useDispatch()
    const navigation = useNavigation()
    const route = useRoute()
    const { sendLiveKitData } = useLiveKitEventSender()

    const soundModeIds = useSelector(
        (state) => state.liveMode?.soundModeIds ?? [],
    )
    const liveParticipants = useSelector(
        (state) => state.liveMode?.liveParticipants ?? [],
    )

    const studentIds = useMemo(
        () => (students ?? []).map((s) => s?.id).filter(Boolean),
        [students],
    )

    // Always returns Promise<boolean> so callers can sequence success-only side
    // effects (markResolved, onPinStudent) after the atomic mutex resolves.
    // Resolves false for the offline pre-check and for API failures.
    const pinAndSetDialogue = useCallback(
        (studentId) => {
            const student = students?.find((s) => s?.id === studentId)
            const displayName = displayNameOf(student)

            if (!liveParticipants.includes(studentId)) {
                SnackBarM.show({
                    text1: `${displayName} est hors-ligne`,
                    duration: 3500,
                })
                return Promise.resolve(false)
            }

            const others = soundModeIds.filter(
                (id) => studentIds.includes(id) && id !== studentId,
            )

            return dispatch(
                pinStudentInDialogueAtomic({
                    liveSessionId,
                    studentId,
                    otherStudentsInDialogue: others,
                }),
            ).then((result) => {
                if (!result?.ok) {
                    // A toggle for this student is already running — stay silent
                    // rather than show a misleading error on the guarded click.
                    if (result?.inFlight) return false
                    SnackBarM.show({
                        text1:
                            result?.message ??
                            `Impossible de basculer ${displayName} en Dialogue`,
                        duration: 3000,
                    })
                    return false
                }
                if (typeof onPinStudent === "function") {
                    onPinStudent(studentId)
                }
                return true
            })
        },
        [
            dispatch,
            liveSessionId,
            soundModeIds,
            liveParticipants,
            studentIds,
            students,
            onPinStudent,
        ],
    )

    const sendSam = useCallback(
        (studentId, eventType) => {
            const checkpointType = checkpointTypeForEvent(eventType)
            if (!checkpointType) return

            dispatch(setOngoingCheckpointIds([studentId], "add"))
            // Record the tutor-initiated Sam send (persisted) so the card shows
            // the passive "Sam aide X" state and the 4-min escalation clock is
            // re-based on this moment rather than on the original hand-raise.
            dispatch(markTutorEventSamSent(`${eventType}-${studentId}`))

            sendLiveKitData(
                "checkpointTriggerResponse",
                {
                    studentId,
                    accepted: true,
                    tutorId: currentUser?.id,
                    trigger_context: checkpointType,
                    trigger_source: "tutor_events_panel",
                    timestamp: Date.now(),
                },
                [studentId],
            )
        },
        [dispatch, sendLiveKitData, currentUser?.id],
    )

    const openStudentActivity = useCallback(
        (studentId, activityId, eventType) => {
            const student = students?.find((s) => s?.id === studentId)
            const displayName = displayNameOf(student)
            if (!liveParticipants.includes(studentId)) {
                SnackBarM.show({
                    text1: `${displayName} est hors-ligne`,
                    duration: 3500,
                })
                return false
            }
            // Pre-select the exact activity so the focus layout opens it
            // (TutorFocusLayoutM reads currentStudentActivity), then focus the
            // tile via onPinStudent — WITHOUT changing anyone's sound mode.
            // Clear the matching source signal so the card retires instead of
            // re-deriving once the resolved window elapses.
            if (activityId) {
                dispatch(setCurrentStudentActivity(studentId, activityId))
                // Explicit focus request (bumps a requestId) so the focus
                // layout selects this activity even when the passive LiveKit
                // attribute sync already set currentStudentActivity to it —
                // e.g. an auto-mode activity the student is already on.
                dispatch(requestStudentActivityFocus(studentId, activityId))
            }
            if (typeof onPinStudent === "function") {
                onPinStudent(studentId)
            }
            if (eventType === EVENT_TYPES.AUTO_ACTIVITY) {
                dispatch(clearAutoModeStartedActivity(studentId))
            } else {
                dispatch(clearRecentFinalizedActivity(studentId))
            }
            return true
        },
        [dispatch, liveParticipants, onPinStudent, students],
    )

    const notifyStudent = useCallback(
        (studentId) => {
            if (IS_WEB) {
                return Promise.resolve(pinAndSetDialogue(studentId))
            }
            const student = students?.find((s) => s?.id === studentId)
            const firstname = student?.firstname ?? "l'élève"
            dispatch(
                apiRequest(
                    liveServiceV2.makeCallV2,
                    { id: liveSessionId },
                    { user_ids: [studentId] },
                ),
            )
            SnackBarM.show({
                text1: `📞 Notification envoyée à ${firstname}`,
                duration: 2500,
            })
            return Promise.resolve(true)
        },
        [dispatch, liveSessionId, pinAndSetDialogue, students],
    )

    const openAgentSummary = useCallback(
        (studentId) => {
            navigation?.setParams({
                ...route?.params,
                agentCheckpointsUserId: studentId,
            })
        },
        [navigation, route?.params],
    )

    const dismissEvent = useCallback(
        (event) => {
            if (!event?.hash) return
            dispatch(dismissTutorEvent(event.hash))
            if (event.studentId) {
                if (event.type === EVENT_TYPES.EXERCISE_DONE) {
                    dispatch(clearRecentFinalizedActivity(event.studentId))
                } else if (event.type === EVENT_TYPES.AUTO_ACTIVITY) {
                    dispatch(clearAutoModeStartedActivity(event.studentId))
                }
            }
        },
        [dispatch],
    )

    const runEventAction = useCallback(
        (event, { markResolved } = {}) => {
            if (!event?.cta?.action) return
            const firstname = event.student?.firstname ?? "L'élève"
            const resolveOnSuccess = (promise, label) => {
                if (typeof markResolved !== "function") return
                Promise.resolve(promise).then((ok) => {
                    if (ok) markResolved(event, label)
                })
            }
            switch (event.cta.action) {
                case "pinAndSetDialogue":
                    resolveOnSuccess(
                        pinAndSetDialogue(event.studentId),
                        `Tu parles à ${firstname}`,
                    )
                    break
                case "sendSam":
                    // samSent state is derived from ongoingCheckpointIds,
                    // which sendSam populates below — no markSamSent needed.
                    sendSam(event.studentId, event.type)
                    break
                case "openStudentActivity":
                    resolveOnSuccess(
                        openStudentActivity(
                            event.studentId,
                            event.activityId,
                            event.type,
                        ),
                        event.type === EVENT_TYPES.AUTO_ACTIVITY
                            ? `Énoncé de ${firstname} ouvert`
                            : `Rendu de ${firstname} ouvert`,
                    )
                    break
                case "notifyStudent":
                    resolveOnSuccess(
                        notifyStudent(event.studentId),
                        `${firstname} notifié`,
                    )
                    break
                case "openAgentSummary":
                    openAgentSummary(event.studentId)
                    break
                default:
                    break
            }
        },
        [
            pinAndSetDialogue,
            sendSam,
            openStudentActivity,
            notifyStudent,
            openAgentSummary,
        ],
    )

    return {
        pinAndSetDialogue,
        sendSam,
        openStudentActivity,
        notifyStudent,
        openAgentSummary,
        dismissEvent,
        runEventAction,
    }
}
